import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  DatePicker,
  Select,
  InputNumber,
  message,
  Space,
  Row,
  Col,
  Divider,
  Typography,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { trimFormValues } from '../../utils/trimFormValues';

const { Option } = Select;
const { Title } = Typography;

const PaymentCreate = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [plPosition, setPlPosition] = useState('income');
  const [drivers, setDrivers] = useState([]);
  const [vans, setVans] = useState([]);

  useEffect(() => {
    // Load drivers and vans
    const loadData = async () => {
      try {
        const [driversRes, vansRes] = await Promise.all([
          apiService.drivers.getAll(),
          apiService.vans.getAll()
        ]);
        setDrivers(driversRes.data);
        setVans(vansRes.data);
      } catch (error) {
        console.error('Error loading data:', error);
        message.error('Failed to load drivers or vans');
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    // Generate reference number when component mounts
    const generateReference = async () => {
      try {
        const response = await apiService.payments.generateInvoiceNumber();
        form.setFieldsValue({ reference: response.data.reference });
      } catch (error) {
        console.error('Error generating reference:', error);
        message.error('Failed to generate reference');
      }
    };
    generateReference();
  }, [form]);

  const calculateVAT = (netAmount, vatRate) => {
    return (netAmount * vatRate) / 100;
  };

  const calculateTotal = (netAmount, vatAmount) => {
    return netAmount + vatAmount;
  };

  const handleAmountChange = (value) => {
    // Only calculate if value exists and is not 0
    if (value && value > 0) {
      const vatRate = form.getFieldValue('vatRate');
      // If VAT rate is 0 or not provided, set VAT amount to 0
      if (!vatRate || vatRate === 0) {
        form.setFieldsValue({
          vatAmount: 0,
          totalAmount: value
        });
        return;
      }
      
      // Only calculate if we have a valid VAT rate
      if (vatRate !== undefined && vatRate !== null) {
        const vatAmount = calculateVAT(value, vatRate);
        const totalAmount = calculateTotal(value, vatAmount);
        
        form.setFieldsValue({
          vatAmount,
          totalAmount
        });
      }
    } else {
      // Reset VAT and total amounts if amount is 0 or cleared
      form.setFieldsValue({
        vatAmount: 0,
        totalAmount: 0
      });
    }
  };

  const handleVatRateChange = (value) => {
    const amount = form.getFieldValue('amount');
    // If VAT rate is 0 or not provided, set VAT amount to 0
    if (!value || value === 0) {
      form.setFieldsValue({
        vatAmount: 0,
        totalAmount: amount || 0
      });
      return;
    }
    
    // Only calculate if amount exists and is not 0
    if (amount && amount > 0) {
      const vatAmount = calculateVAT(amount, value);
      const totalAmount = calculateTotal(amount, vatAmount);
      
      form.setFieldsValue({
        vatAmount,
        totalAmount
      });
    } else {
      // Reset VAT and total amounts if no amount is entered
      form.setFieldsValue({
        vatAmount: 0,
        totalAmount: 0
      });
    }
  };

  const handlePlPositionChange = (value) => {
    setPlPosition(value);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const trimmedValues = trimFormValues(values);
      const formattedValues = {
        ...trimmedValues,
        plPosition,
        dueDate: trimmedValues.dueDate?.toISOString(),
        amount: Number(trimmedValues.amount),
        vatAmount: Number(trimmedValues.vatAmount),
        totalAmount: Number(trimmedValues.totalAmount),
        status: trimmedValues.status || 'pending',
      };
      
      const response = await apiService.payments.create(formattedValues);
      
      if (response.data) {
        message.success('Payment created successfully');
        navigate('/payments');
      } else {
        throw new Error(response.data.message || 'Failed to create payment');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      message.error(error.response?.data?.message || 'Failed to create payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/payments')}
            >
              Back
            </Button>
            <span>Create New Payment</span>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            status: 'pending',
            paymentMethod: 'bank_transfer',
            vatRate: 20,
          }}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Title level={4}>Payment Details</Title>
            </Col>

            <Col span={8}>
              <Form.Item
                name="plPosition"
                label="PL Position"
                rules={[{ required: true, message: 'Please select PL position' }]}
              >
                <Select onChange={handlePlPositionChange}>
                  <Option value="income">Income</Option>
                  <Option value="expense">Expense</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="reference"
                label="Reference"
                rules={[{ required: true, message: 'Reference is required' }]}
              >
                <Input disabled />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select>
                  <Option value="fuel">Fuel</Option>
                  <Option value="maintenance">Maintenance</Option>
                  <Option value="insurance">Insurance</Option>
                  <Option value="insurance claim">Insurance Claim</Option>
                  <Option value="salary">Salary</Option>
                  <Option value="van rental">Van Rental</Option>
                  <Option value="van deposits">Van Deposits</Option>
                  <Option value="tracking charges">Tracking Charges</Option>
                  <Option value="office">Office</Option>
                  <Option value="job">Job</Option>
                  <Option value="charges">Charges</Option>
                  <Option value="porter">Porter</Option>
                  <Option value="accounting services">Accounting Services</Option>
                  <Option value="paye">PAYE</Option>
                  <Option value="excess mileage">Excess Mileage</Option>
                  <Option value="vat liability">VAT Liability</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="dueDate"
                label="Due Date"
                rules={[{ required: true, message: 'Please select due date' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select due date"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="amount"
                label="Net Amount (£)"
                rules={[{ required: true, message: 'Please enter amount' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  onChange={handleAmountChange}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="vatRate"
                label="VAT Rate (%)"
                rules={[{ required: true, message: 'Please enter VAT rate' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  step={0.1}
                  precision={1}
                  onChange={handleVatRateChange}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="vatAmount"
                label="VAT Amount (£)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                  precision={2}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="totalAmount"
                label="Total Amount (£)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                  precision={2}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider>Related Information</Divider>
            </Col>

            <Col span={8}>
              <Form.Item
                name="relatedDriver"
                label="Link to Driver"
                tooltip="Link this payment to a specific driver"
              >
                <Select
                  allowClear
                  placeholder="Select driver"
                  showSearch
                  optionFilterProp="children"
                >
                  {drivers.map(driver => (
                    <Option key={driver._id} value={driver._id}>
                      {driver.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="relatedVan"
                label="Link to Vehicle"
                tooltip="Link this payment to a specific vehicle"
              >
                <Select
                  allowClear
                  placeholder="Select vehicle"
                  showSearch
                  optionFilterProp="children"
                >
                  {vans.map(van => (
                    <Option key={van._id} value={van._id}>
                      {van.registration} ({van.make} {van.model})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="paymentMethod"
                label="Payment Method"
                rules={[{ required: true, message: 'Please select payment method' }]}
              >
                <Select>
                  <Option value="cash">Cash</Option>
                  <Option value="bank_transfer">Bank Transfer</Option>
                  <Option value="credit_card">Credit Card</Option>
                  <Option value="debit_card">Debit Card</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Option value="pending">Pending</Option>
                  <Option value="paid">Paid</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={16}>
              <Form.Item
                name="receipt"
                label="Receipt URL"
              >
                <Input placeholder="Enter URL to receipt image/document" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="notes"
                label="Notes"
              >
                <Input.TextArea rows={3} placeholder="Enter any additional notes about this payment" />
              </Form.Item>
            </Col>

            <Col span={24} style={{ textAlign: 'right', marginTop: 16 }}>
              <Space>
                <Button onClick={() => navigate('/payments')}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  Create Payment
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default PaymentCreate; 